import React, { useEffect, useMemo, useState } from 'react';
import { useImmer } from 'use-immer';
import {
  DeepPartial,
  ServiceProviderAssignmentErrors,
} from '../../common/assignment/interfaces';
import { validateSpAssignment } from './sp-assignment-validation';
import { SpAssignmentProgressModal } from './sp-assignment-progress-modal';
import {
  AssignmentModel,
  AssignmentStatusType,
  CitizenDto,
  CompanyLookupModel,
  FrequencyForReportingModel,
  GrantTypeOfferCombinationModel,
  ServiceProviderUnitModel,
} from '../../../../api-service/clients';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { useMetaTitle, useQuery } from '../../../../hooks';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import {
  toGuardianTypeModel,
  GuardianTypeValue,
  toRepresentationTypeModel,
  RepresentationTypeValue,
} from '../../authority/assessment/components/utils';
import sanitizeContent from '../../../../components/editor/sanitize-content';
import { useSpuIdFromUrl } from '../../hooks/use-spu-id-from-url';
import { useCurrentUserResponsible } from './use-current-user-responsible';
import { useServiceProviderUnit } from './use-service-provider-unit';
import { useGetGrantTypes } from '../../common/assignment/use-get-grant-types';
import { useGrantTypeOfferCombinations } from './use-grant-type-offer-combinations';
import { isGrantTypeOfferCombinationSelectable } from './grant-type-offer-combination-utils';
import {
  BenefitType,
  TargetGroupType,
  useGetBenefitTypes,
} from '../../common/assignment/use-get-benefit-types';
import { AssessmentInformation } from '../../common/assignment/assessment-information';
import { Assignment } from '../../common/assignment/assignment';
import { BackgroundInformation } from '../../common/assignment/background-information';
import { CoordinationDetails } from '../../common/assignment/coordination-details';
import { SpCommissionedIntervention } from './sp-commissioned-intervention';
import { today, toStartOfDay } from '../../../../utilities';
import { useCreateAssignmentAsServiceProvider } from './use-create-assignment-as-service-provider';

const spReceiveCitizenClient =
  moduleClientFactory.createCitizenServiceProviderReceiveCitizenClient(true);
type RichTextSection =
  | 'backgroundInformation'
  | 'coordinationDetails'
  | 'assessmentInformation';
type RichTextEditor = { getContent: (options: { format: 'text' }) => string };

export function SpCreateAssignment() {
  const spuId = useSpuIdFromUrl();
  const cpr = useQuery('cpr');

  const [citizen, setCitizen] = useState<CitizenDto>();
  const [errors, setErrors] = useState<ServiceProviderAssignmentErrors>(
    {} as ServiceProviderAssignmentErrors
  );
  const [activityOfferId, setActivityOfferId] = useState<string | undefined>();
  const [assignment, setAssignment] = useImmer<DeepPartial<AssignmentModel>>({
    status: AssignmentStatusType.Draft,
    backgroundInformation: {
      serviceProviderUnitId: spuId ? { value: spuId } : undefined,
    },
    coordinationDetails: {},
    assessmentInformation: {},
    commissionedIntervention: {
      reportingFrequency: FrequencyForReportingModel.NoReportingMeasurement,
    },
  });
  const responsibleUser = useCurrentUserResponsible();
  const serviceProviderUnit = useServiceProviderUnit(spuId);
  const grantTypes = useGetGrantTypes();
  const { benefitTypes, targetGroupTypes } = useGetBenefitTypes();
  const companyId = assignment.backgroundInformation?.companyId?.value;
  const { combinations: grantTypeOfferCombinations } =
    useGrantTypeOfferCombinations(companyId);

  const [progressModalOpen, setProgressModalOpen] = useState(false);

  const {
    progressSteps,
    resetProgressSteps,
    resetFailedProgressSteps,
    executeSteps,
    navigateToReceiveAssignment,
    resetIntermediateState,
  } = useCreateAssignmentAsServiceProvider({
    cpr,
    spuId,
    activityOfferId,
    assignment,
    serviceProviderUnitName: serviceProviderUnit?.name,
  });

  useMetaTitle(`Opret bestilling - ${citizen?.fullName}`);

  useEffect(() => {
    if (!cpr) return;
    async function fetchCitizen() {
      const response =
        await spReceiveCitizenClient.getCitizenFromFasitOrExternalSource({
          citizenCpr: cpr,
        });
      setCitizen(response.citizen);
      setAssignment((draft: DeepPartial<AssignmentModel>) => {
        const citizenId = response.citizen?.citizenId;
        if (citizenId === '00000000-0000-0000-0000-000000000000') {
          delete draft.citizenId;
        } else {
          draft.citizenId = { value: citizenId };
        }
      });
    }
    fetchCitizen();
  }, [cpr]);

  useEffect(() => {
    if (
      grantTypeOfferCombinations.length === 1 &&
      !assignment.commissionedIntervention?.grantTypeId &&
      isGrantTypeOfferCombinationSelectable(grantTypeOfferCombinations[0])
    ) {
      const only = grantTypeOfferCombinations[0];
      setActivityOfferId(only.offerId);
      setAssignment((draft: AssignmentModel) => {
        draft.commissionedIntervention.grantTypeId = only.grantTypeId;
        draft.commissionedIntervention.socialOfferFFBId = only.socialOfferFfbId;
      });
    }
  }, [
    assignment.commissionedIntervention?.grantTypeId,
    grantTypeOfferCombinations,
  ]);

  useEffect(() => {
    if (
      responsibleUser &&
      !assignment.backgroundInformation?.responsibleUserId
    ) {
      setAssignment((draft: AssignmentModel) => {
        if (draft.backgroundInformation) {
          draft.backgroundInformation.responsibleUserId = {
            value: responsibleUser.userId,
          };
          draft.backgroundInformation.responsibleTeamId = responsibleUser.teamId
            ? { value: responsibleUser.teamId }
            : undefined;
        }
      });
    }
  }, [assignment.backgroundInformation?.responsibleUserId, responsibleUser]);

  useEffect(() => {
    if (!spuId || !serviceProviderUnit) return;

    setAssignment((draft: AssignmentModel) => {
      if (draft.backgroundInformation) {
        draft.backgroundInformation.serviceProviderUnitId = { value: spuId };
        draft.backgroundInformation.serviceProviderUnitName =
          serviceProviderUnit.name;
        draft.backgroundInformation.companyId = {
          value: serviceProviderUnit.companyId,
        };
      }
    });
  }, [serviceProviderUnit, spuId]);

  function handleValidate() {
    const validationErrors = validateSpAssignment({
      backgroundInformation: assignment.backgroundInformation,
      assessmentInformation: assignment.assessmentInformation,
      commissionedIntervention: assignment.commissionedIntervention,
      activityOfferId,
    });
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }

  async function handleSubmit() {
    setErrors({} as ServiceProviderAssignmentErrors);
    if (!handleValidate()) {
      return;
    }

    resetProgressSteps();
    setProgressModalOpen(true);
    await executeSteps();
  }

  function handleRetry() {
    resetFailedProgressSteps();
    executeSteps();
  }

  function handleCloseProgressModal() {
    setProgressModalOpen(false);
    resetIntermediateState();
  }

  function handleSocialProviderUnitChanged(
    nextSpu: ServiceProviderUnitModel | undefined
  ) {
    setAssignment((draft: AssignmentModel) => {
      if (draft.backgroundInformation) {
        draft.backgroundInformation.serviceProviderUnitId =
          nextSpu?.serviceProviderUnitId
            ? { value: nextSpu.serviceProviderUnitId.value }
            : { value: '' };
      }
      draft.backgroundInformation.serviceProviderUnitName = nextSpu?.name;
    });
  }

  function handleNameChange(value: string) {
    setAssignment((draft: AssignmentModel) => {
      if (draft.backgroundInformation) {
        draft.backgroundInformation.name = value;
      }
    });
  }

  function handleCompanyChange(value?: CompanyLookupModel) {
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.companyId = { value: value?.id ?? '' };
    });
  }

  function handleResponsibleUserChange(userId?: string, teamId?: string) {
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.responsibleUserId = userId
        ? { value: userId }
        : undefined;
      draft.backgroundInformation.responsibleTeamId = teamId
        ? { value: teamId }
        : undefined;
    });
  }

  function handleRichTextChange(
    section: RichTextSection,
    fieldName: string,
    event: unknown,
    editor: RichTextEditor | undefined
  ) {
    if (typeof event !== 'string') {
      return;
    }
    const contentPlainText = editor?.getContent({ format: 'text' }) ?? '';
    setAssignment(draft => {
      const assignmentSection = draft[section] as Record<
        string,
        string | undefined
      >;
      assignmentSection[`${fieldName}RichText`] = sanitizeContent(event);
      assignmentSection[fieldName] = sanitizeContent(contentPlainText);
    });
  }

  function handleHasGuardianChange(value: YesNo) {
    let boolValue: boolean | undefined;
    if (value === YesNo.Yes) {
      boolValue = true;
    } else if (value === YesNo.No) {
      boolValue = false;
    } else {
      boolValue = undefined;
    }
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.hasGuardian = boolValue;
      if (boolValue !== true) {
        draft.backgroundInformation.guardianType = undefined;
        draft.backgroundInformation.guardianDetails = undefined;
      }
    });
  }

  function handleGuardianTypeChange(value?: GuardianTypeValue) {
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.guardianType = toGuardianTypeModel(value);
    });
  }

  function handleGuardianDetailsChange(value: string) {
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.guardianDetails = value;
    });
  }

  function handleIsRepresentedChange(value: YesNo) {
    let boolValue: boolean | undefined;
    if (value === YesNo.Yes) {
      boolValue = true;
    } else if (value === YesNo.No) {
      boolValue = false;
    } else {
      boolValue = undefined;
    }
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.isRepresented = boolValue;
      if (boolValue !== true) {
        draft.backgroundInformation.representationType = undefined;
      }
    });
  }

  function handleRepresentationTypeChange(value?: RepresentationTypeValue) {
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.representationType =
        toRepresentationTypeModel(value);
    });
  }

  function handlePowersOfAttorneyChange(value: string) {
    setAssignment((draft: AssignmentModel) => {
      draft.backgroundInformation.powersOfAttorney = value;
    });
  }

  function handleAgreementsBetweenCitizenAndRelativesChange(value: string) {
    setAssignment((draft: AssignmentModel) => {
      draft.coordinationDetails.agreementsBetweenCitizenAndRelatives = value;
    });
  }

  function handlePrimaryTargetGroupChange(value?: TargetGroupType) {
    setAssignment((draft: AssignmentModel) => {
      const primaryId = value?.id;
      draft.assessmentInformation.primaryAssessmentTopicId = primaryId;
      if (primaryId) {
        const additional =
          draft.assessmentInformation.additionalAssessmentTopicIds;
        if (additional?.some(id => id === primaryId)) {
          draft.assessmentInformation.additionalAssessmentTopicIds =
            additional.filter(id => id !== primaryId);
        }
      }
    });
  }

  function handleAdditionalTargetGroupChange(values: TargetGroupType[]) {
    setAssignment((draft: AssignmentModel) => {
      draft.assessmentInformation.additionalAssessmentTopicIds = values.map(
        v => v.id
      );
    });
  }

  function handleReportingFrequencyChange(value?: FrequencyForReportingModel) {
    setAssignment((draft: AssignmentModel) => {
      draft.commissionedIntervention.reportingFrequency = value;
    });
  }

  function handleDaysBetweenMeasurementsChange(value?: number) {
    setAssignment((draft: AssignmentModel) => {
      draft.commissionedIntervention.daysBetweenMeasurements = value;
    });
  }

  function handleFollowUpDateChange(value?: Date) {
    setAssignment((draft: AssignmentModel) => {
      draft.commissionedIntervention.followUpDate = value;
    });
  }

  function handleBenefitTypesChange(values: BenefitType[]) {
    setAssignment((draft: AssignmentModel) => {
      draft.commissionedIntervention.benefitTypeIds = values.map(v => v.id);
    });
  }

  function handleGrantTypeOfferChange(value?: GrantTypeOfferCombinationModel) {
    setActivityOfferId(value?.offerId);
    setErrors(prev => ({ ...prev, benefitTypeIds: undefined }));
    setAssignment((draft: AssignmentModel) => {
      draft.commissionedIntervention.grantTypeId = value?.grantTypeId || '';
      draft.commissionedIntervention.socialOfferFFBId = value?.socialOfferFfbId;
      draft.commissionedIntervention.benefitTypeIds = [];
    });
  }

  function handleAgreementsBetweenAuthorityAndProviderChange(value: string) {
    setAssignment((draft: AssignmentModel) => {
      draft.commissionedIntervention.agreementsBetweenAuthorityAndProvider =
        value;
    });
  }

  const filteredBenefitTypes = useMemo(() => {
    const selectedGrantTypeId =
      assignment.commissionedIntervention?.grantTypeId;
    const selectedGrantType = grantTypes.find(
      grantType => grantType.grantTypeId === selectedGrantTypeId
    );
    if (!selectedGrantType) return [];

    const selectedGrantTypeFFBBenefits =
      selectedGrantType.allowedBenefitTypesFFB;
    const selectedGrantTypeDSTBenefits =
      selectedGrantType.allowedBenefitTypesDST;
    return benefitTypes.filter(
      benefitType =>
        selectedGrantTypeFFBBenefits?.some(ffb => ffb.id === benefitType.id) ||
        selectedGrantTypeDSTBenefits?.some(dst => dst.id === benefitType.id)
    );
  }, [
    assignment.commissionedIntervention?.grantTypeId,
    benefitTypes,
    grantTypes,
  ]);

  const progressModalProps = {
    open: progressModalOpen,
    steps: progressSteps,
    onRetry: handleRetry,
    onClose: handleCloseProgressModal,
    onGoToReceive: navigateToReceiveAssignment,
  };

  const {
    backgroundInformation,
    coordinationDetails,
    assessmentInformation,
    commissionedIntervention,
  } = assignment;
  const assignmentStartDate = backgroundInformation?.startDate
    ? new Date(backgroundInformation.startDate)
    : undefined;
  const assignmentEndDate = backgroundInformation?.endDate
    ? new Date(backgroundInformation.endDate)
    : undefined;
  const todayStart = today();
  const followUpMinDate = assignmentStartDate
    ? toStartOfDay(assignmentStartDate)
    : todayStart;
  const followUpMaxDate = assignmentEndDate;
  const serviceProviderUnits = serviceProviderUnit ? [serviceProviderUnit] : [];

  return (
    <>
      <Assignment
        isEditmode={false}
        isReadOnly={false}
        isLoading={progressModalOpen}
        status={AssignmentStatusType.Draft}
        assignment={assignment}
        onSaveAssignment={handleSubmit}
        isSocialProviderContext
        backgroundInformation={
          <BackgroundInformation
            backgroundInformation={backgroundInformation || {}}
            isReadOnly={false}
            lockServiceProviderUnit={false}
            isSocialProviderContext
            errors={errors}
            serviceProviderUnits={serviceProviderUnits}
            activityHasSpu={false}
            activityHasCompany={false}
            onNameChange={handleNameChange}
            onCompanyChange={handleCompanyChange}
            onSocialProviderUnitChanged={handleSocialProviderUnitChanged}
            onResponsibleUserChange={handleResponsibleUserChange}
            onRichTextChange={handleRichTextChange}
            onHasGuardianChange={handleHasGuardianChange}
            onGuardianTypeChange={handleGuardianTypeChange}
            onGuardianDetailsChange={handleGuardianDetailsChange}
            onIsRepresentedChange={handleIsRepresentedChange}
            onRepresentationTypeChange={handleRepresentationTypeChange}
            onPowersOfAttorneyChange={handlePowersOfAttorneyChange}
            showActivityLink={false}
            hideActivityField
            companyReadOnly
            spuReadOnly
          />
        }
        coordinationDetails={
          <CoordinationDetails
            coordinationDetails={coordinationDetails || {}}
            isReadOnly={false}
            errors={errors}
            onRichTextChange={handleRichTextChange}
            onAgreementsBetweenCitizenAndRelativesChange={
              handleAgreementsBetweenCitizenAndRelativesChange
            }
          />
        }
        assessmentInformation={
          <AssessmentInformation
            assessmentInformation={assessmentInformation || {}}
            isReadOnly={false}
            targetGroupTypes={targetGroupTypes}
            errors={errors}
            onRichTextChange={handleRichTextChange}
            onPrimaryTargetGroupChange={handlePrimaryTargetGroupChange}
            onAdditionalTargetGroupsChange={handleAdditionalTargetGroupChange}
          />
        }
        commissionedIntervention={
          <SpCommissionedIntervention
            commissionedIntervention={commissionedIntervention || {}}
            activityOfferId={activityOfferId}
            isReadOnly={false}
            isEditmode={false}
            agreementsRequired
            errors={errors}
            followUpMinDate={followUpMinDate}
            followUpMaxDate={followUpMaxDate}
            grantTypeOfferCombinations={grantTypeOfferCombinations}
            benefitTypes={filteredBenefitTypes}
            interventionObjectives={[]}
            interventionObjectivesIsLoading={false}
            onGrantTypeOfferChange={handleGrantTypeOfferChange}
            onReportingFrequencyChange={handleReportingFrequencyChange}
            onDaysBetweenMeasurementsChange={
              handleDaysBetweenMeasurementsChange
            }
            onFollowUpDateChange={handleFollowUpDateChange}
            onBenefitTypesChange={handleBenefitTypesChange}
            onAgreementsBetweenAuthorityAndProviderChange={
              handleAgreementsBetweenAuthorityAndProviderChange
            }
            onEditInterventionObjective={() => {}}
          />
        }
      />
      <SpAssignmentProgressModal {...progressModalProps} />
    </>
  );
}
